// src/components/PreConsultSummaryView.tsx
// A colorful, glanceable read-only view of a completed Pre-Consult stage —
// used inside StageDataModal instead of that modal's generic key/value dump.
// Stays template-driven like PreConsultStage.jsx itself (fetches the same
// ailment_templates list to recover field labels/sections/types for the
// template the nurse actually used), so this works for any Assessment
// Module, not just Diabetes. Reuses AssessmentSummaryCard as-is — the
// snapshot it renders (data.template_summary) was already computed and
// saved at submit time, so no scoring logic is duplicated here.
import { useEffect, useState, type ReactNode } from 'react';
import { getAilmentTemplates } from '../api';
import { RiskBadge, RISK_LEVEL_TONE, type RiskTone } from './ui/RiskBadge';
import { ValueChip } from './ui/ValueChip';
import AssessmentSummaryCard, { type AssessmentSummary, type TriageColor } from './AssessmentSummaryCard';
import { isEmptyValue, humanize } from '../utils/stageDataRender';

/** A template field, as authored in the Admin Panel's AilmentTemplates
 * builder (fields_config.sections[].fields[]) — loosely typed since the
 * `value`/`suffix`/`items` shape varies per field `type`, same latitude the
 * live PreConsultStage.jsx form itself takes. */
interface TemplateFieldDef {
  key: string;
  label?: string;
  type: string;
  suffix?: string;
  /** `choice-matrix` only — just enough to resolve a row key to its display
   * label; the answer itself (`cells[col].option`) is already a human
   * string, so nothing else here needs the columns/cells shape. */
  rows?: Array<{ key: string; label?: string }>;
}
interface TemplateSection { label?: string; fields: TemplateFieldDef[] }
interface AilmentTemplate {
  id: number;
  fields_config: { sections: TemplateSection[] };
}

/** The pre_consult stage_data shape, loosely — same reasoning as
 * StageSummaryView.tsx's StageData: a JSONB blob shaped by whichever
 * template (if any) the nurse picked at Vitals, not one fixed schema. */
export type PreConsultData = Record<string, any>;

// A light heuristic icon per section/field label — purely decorative, never
// data-bearing (nothing downstream reads it), so a template author naming a
// section something this doesn't recognize just falls back to a pin, never
// breaks.
function sectionIcon(label = ''): string {
  const l = label.toLowerCase();
  if (/diet|meal|plate|protein|fibre|fiber|carb|nutrition/.test(l)) return '🍽️';
  if (/activity|exercise|walk|physical/.test(l)) return '🏃';
  if (/complication|vascular|retino|nephro|neuro|cad|stroke|pad|foot/.test(l)) return '🩺';
  if (/hba1c|glucose|sugar|lab|blood/.test(l)) return '🩸';
  if (/screen|checklist|due/.test(l)) return '📋';
  if (/status|history|treatment/.test(l)) return '📈';
  return '📌';
}

function FieldAnswer({ field, value, tone }: { field: TemplateFieldDef; value: any; tone?: 'success' | 'warn' | 'danger' }): ReactNode {
  if (field.type === 'checkbox-group' || field.type === 'radio' || field.type === 'pictorial-choice') {
    const selected: string[] = (Array.isArray(value) ? value : [value]).filter(Boolean);
    if (!selected.length) return <span style={{ color: 'var(--color-ink-muted)', fontSize: 12.5 }}>—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {selected.map(opt => <ValueChip key={opt} tone={tone || 'neutral'}>{opt}</ValueChip>)}
      </div>
    );
  }
  if (field.type === 'number') {
    return <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-ink)' }}>{value}{field.suffix ? ` ${field.suffix}` : ''}</span>;
  }
  if (field.type === 'vitals') {
    const entries = Object.entries(value || {}).filter(([, v]) => !isEmptyValue(v));
    if (!entries.length) return <span style={{ color: 'var(--color-ink-muted)', fontSize: 12.5 }}>—</span>;
    return (
      <div className="flex flex-wrap gap-3">
        {entries.map(([k, v]) => (
          <span key={k} style={{ fontSize: 12.5 }}>
            <span style={{ color: 'var(--color-ink-muted)' }}>{k}: </span><b>{v as ReactNode}</b>
          </span>
        ))}
      </div>
    );
  }
  if (field.type === 'choice-matrix') {
    const entries = Object.entries(value || {}) as [string, string][];
    if (!entries.length) return <span style={{ color: 'var(--color-ink-muted)', fontSize: 12.5 }}>—</span>;
    const rowLabel = (rowKey: string) => field.rows?.find(r => r.key === rowKey)?.label || rowKey;
    return (
      <div className="flex flex-col gap-1">
        {entries.map(([rowKey, answer]) => (
          <div key={rowKey} style={{ fontSize: 12.5 }}>
            <span style={{ color: 'var(--color-ink-muted)' }}>{rowLabel(rowKey)}: </span><b>{answer}</b>
          </div>
        ))}
      </div>
    );
  }
  if (field.type === 'due-checklist') {
    const entries = Object.entries(value || {}) as [string, { last_done?: string } | undefined][];
    if (!entries.length) return <span style={{ color: 'var(--color-ink-muted)', fontSize: 12.5 }}>—</span>;
    return (
      <div className="flex flex-col gap-1">
        {entries.map(([label, v]) => (
          <div key={label} style={{ fontSize: 12.5 }}>
            <span>{label}</span>
            {v?.last_done
              ? <span style={{ color: 'var(--color-ink-muted)' }}> — {new Date(v.last_done).toLocaleDateString('en-IN')}</span>
              : <span style={{ color: 'var(--color-danger-ink)' }}> — Due</span>}
          </div>
        ))}
      </div>
    );
  }
  return <span style={{ fontSize: 13, color: 'var(--color-ink)' }}>{String(value)}</span>;
}

const FIELD_TONE: Record<TriageColor, 'success' | 'warn' | 'danger'> = { green: 'success', yellow: 'warn', red: 'danger' };

export default function PreConsultSummaryView({ data }: { data: PreConsultData }) {
  const [templates, setTemplates] = useState<AilmentTemplate[] | null>(null);
  useEffect(() => { getAilmentTemplates().then(setTemplates).catch(() => setTemplates([])); }, []);

  const template = data.ailment_template_id && templates
    ? templates.find(t => t.id === data.ailment_template_id)
    : null;

  const fieldTone: Record<string, 'success' | 'warn' | 'danger' | undefined> = {};
  const summary: AssessmentSummary | undefined = data.template_summary;
  (summary?.fields || []).forEach(f => { fieldTone[f.key] = FIELD_TONE[f.color]; });

  const hasTriageStrip = data.risk_level || data.pain_scale || data.allergies || data.current_meds;

  return (
    <div className="flex flex-col gap-3.5">
      {hasTriageStrip && (
        <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-line bg-canvas/60 p-3">
          {data.risk_level && <RiskBadge tone={(RISK_LEVEL_TONE as Record<string, RiskTone>)[data.risk_level] || 'neutral'}>⚠ Risk: {data.risk_level}</RiskBadge>}
          {!isEmptyValue(data.pain_scale) && <ValueChip tone="info">🩹 Pain {data.pain_scale}/10</ValueChip>}
          {data.allergies && <ValueChip tone={/^nkda$/i.test(String(data.allergies).trim()) ? 'success' : 'danger'}>⚠ {data.allergies}</ValueChip>}
          {data.current_meds && <ValueChip tone="neutral">💊 {data.current_meds}</ValueChip>}
        </div>
      )}

      <AssessmentSummaryCard summary={data.template_summary} title={`${data.ailment || 'Assessment'} summary`} />

      {template ? (
        template.fields_config.sections.map((sec, i) => {
          const fieldsWithValue = sec.fields.filter(f => !isEmptyValue(data.template_answers?.[f.key]));
          if (!fieldsWithValue.length) return null;
          return (
            <div key={i} className="rounded-[var(--radius)] border border-line bg-card p-3">
              {sec.label && (
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-muted">
                  {sectionIcon(sec.label)} {sec.label}
                </div>
              )}
              <div className="flex flex-col gap-2.5">
                {fieldsWithValue.map(f => (
                  <div key={f.key}>
                    <div className="mb-1 text-[11.5px] font-semibold text-ink-muted">{f.label}</div>
                    <FieldAnswer field={f} value={data.template_answers[f.key]} tone={fieldTone[f.key]} />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      ) : (
        // Template couldn't be resolved (deactivated/deleted since, or "General/other" —
        // no ailment_template_id at all) — fall back to a plain humanized dump of
        // whatever was actually answered, still inside the same card language.
        !isEmptyValue(data.template_answers) && (
          <div className="rounded-[var(--radius)] border border-line bg-card p-3">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-muted">📌 Assessment answers</div>
            <div className="flex flex-col gap-2">
              {Object.entries(data.template_answers as Record<string, unknown>).filter(([, v]) => !isEmptyValue(v)).map(([k, v]) => (
                <div key={k} style={{ fontSize: 12.5 }}>
                  <span style={{ fontWeight: 600, color: 'var(--color-ink-muted)' }}>{humanize(k)}: </span>
                  <span>{Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}</span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {data.additional_vitals && (
        <div className="rounded-[var(--radius)] border border-line bg-card p-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-ink-muted">📝 Additional observations</div>
          <div style={{ fontSize: 13, color: 'var(--color-ink)' }}>{data.additional_vitals}</div>
        </div>
      )}

      {data.nurse_notes && (
        <div className="rounded-[var(--radius)] border border-warn/40 bg-warn-soft p-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-warn-ink">🗒 Nurse notes</div>
          <div style={{ fontSize: 13, color: 'var(--color-warn-ink)' }}>{data.nurse_notes}</div>
        </div>
      )}
    </div>
  );
}
